// SORTING FUNCTIONS

// check of row is a preregistered student
export const isPreregisteredStudent = (row: any) => {
  return row.original.hasOwnProperty("email");
};

// custom sorting for numeric values
// students with data at the top & in order
// students without data in the middle
// preregistered students at the bottom
export const sortNumbers = (rowA: any, rowB: any, columnId: string, desc?: boolean) => {
  // preregistered students need to be below students without data, so they will have their value set to -/+ infinity.
  // registered students without data will be set to Number.MIN_SAFE_INTEGER/Number.MAX_SAFE_INTEGER,
  // because that will be lower/greater than any student value, but less/greater than preregistered students
  const infinityValue = desc ? -Infinity : Infinity;
  const almostInfinityValue = desc ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;

  // if preregistered student, use infinity value
  // if registered, but data does not exist, use ALMOST infinity value
  // if registered & value does exist, use value
  const a: number = isPreregisteredStudent(rowA)
    ? infinityValue
    : rowA.values[columnId] === undefined
    ? almostInfinityValue
    : rowA.values[columnId];
  const b: number = isPreregisteredStudent(rowB)
    ? infinityValue
    : rowB.values[columnId] === undefined
    ? almostInfinityValue
    : rowB.values[columnId];

  // if values equal each other, return 0
  if (a === b) {
    return 0;
  }
  // if a is larger than b, return 1
  // if a is smaller than b, return -1
  return a > b ? 1 : -1;
};

// custom sorting for string values
// students with data at the top & in order
// students without data in the middle
// preregistered students at the bottom
export const sortStrings = (rowA: any, rowB: any, columnId: string, desc?: boolean) => {
  // by using an empty string '' or 'z'x50 (depending on sorting order),
  // preregistered students will remain on the bottom
  const infinityValue = desc ? "" : "zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzz";

  // if preregistered student, use infinity value
  // if registered, use value
  const a: string = isPreregisteredStudent(rowA) ? infinityValue : rowA.values[columnId];
  const b: string = isPreregisteredStudent(rowB) ? infinityValue : rowB.values[columnId];

  // Split on the comma
  const aArray = a.split(",");
  const bArray = b.split(",");

  // check if last names are equal
  if (aArray?.[0]?.toLowerCase() === bArray?.[0]?.toLowerCase()) {
    // check if first names exist on both
    if (aArray.length === 2 && bArray.length === 2) {
      // if first names are equal, return 0
      if (aArray?.[1]?.toLowerCase() === bArray?.[1]?.toLowerCase()) {
        return 0;
      }
      // compare first names
      return (aArray?.[1]?.toLowerCase() || 0) > (bArray?.[1]?.toLowerCase() || 0) ? 1 : -1;
    }
    // if first names don't exist on both, return 0
    return 0;
  }
  // compare last names
  return (aArray?.[0]?.toLowerCase() || 0) > (bArray?.[0]?.toLowerCase() || 0) ? 1 : -1;
};

// custom sorting for date values,
// so students never signed in remain on the bottom
export const sortDates = (rowA: any, rowB: any, columnId: string, desc?: boolean) => {
  // by using new Date(0) or one day in the future (depending on sorting order),
  // preregistered students will remain on the bottom
  // 864e5 is the number of milliseconds in one day
  // almostInfinityValue is half a day less in the past/future than the infinity value
  const infinityValue = desc ? new Date(0) : new Date(Date.now() + 864e5);
  const almostInfinityValue = desc ? new Date(432e5) : new Date(Date.now() + 432e5);

  // if preregistered, use infinity value
  // if login date for the student does not exist, use ALMOST infinity value
  // if login does exist, use value
  const a: Date = isPreregisteredStudent(rowA)
    ? infinityValue
    : rowA.values[columnId] === undefined
    ? almostInfinityValue
    : rowA.values[columnId];
  const b: Date = isPreregisteredStudent(rowB)
    ? infinityValue
    : rowB.values[columnId] === undefined
    ? almostInfinityValue
    : rowB.values[columnId];

  // if date/times equal each other, return 0
  if (a.getTime() === b.getTime()) {
    return 0;
  }
  // if a is later than b, return 1
  // if a is earlier than b, return -1
  return a.getTime() > b.getTime() ? 1 : -1;
};

// custom sorting for grades
export const sortGrades = (rowA: any, rowB: any, columnId: string, desc?: boolean) => {
  // preregistered students need to be below students without data, so they will have there value set to -/+ infinity.
  // registered students without data will be set to Number.MIN_SAFE_INTEGER/Number.MAX_SAFE_INTEGER,
  // because that will be lower/greater than any student value, but less/greater than preregistered students
  const infinityValue = desc ? -Infinity : Infinity;
  const almostInfinityValue = desc ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  // array of grades in the correct order
  const grades = ["Pk", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

  // ********** not checking for if value exists, but is not a valid grade (aka not in array)
  // if preregistered, set to infinity value
  // if grade in exists, get index in grades array, else set to ALMOST infinity value
  const a = isPreregisteredStudent(rowA)
    ? infinityValue
    : rowA.values[columnId] === undefined
    ? almostInfinityValue
    : grades.indexOf(rowA.values[columnId]);
  const b = isPreregisteredStudent(rowB)
    ? infinityValue
    : rowB.values[columnId] === undefined
    ? almostInfinityValue
    : grades.indexOf(rowA.values[columnId]);

  // if grades are the same, return 0
  if (a === b) return 0;
  // compare indexes of grade values in grades array
  // if the grade from rowA appears further in the grades array, return 1
  // if the grade from rowA appears closer to the start in the grades array, return -1
  return a > b ? 1 : -1;
};

// FORMATTING FUNCTIONS

// format number of seconds to minutes & hours
export const formatTime = (seconds: number) => {
  // get hours
  const hours = Math.floor(seconds / 3600);
  // get minutes
  const minutes = Math.ceil((seconds % 3600) / 60);

  const hourText = `${hours > 1 ? "hours" : "hour"}`;

  return `${hours !== 0 ? `${hours} ${hourText} ` : ""}${minutes !== 0 ? `${minutes} Min` : ""}`;
};

// format last login to display 'online', 'never', or formatted date
export const formatLastLogin = (props: any) => {
  // console.log(new Date(props.value).getTime(), new Date(0), new Date(props.value) === new Date(0))
  // if lastLogin does not exist return empty string
  if (!props.value) return <span style={{ color: "#BF3824" }}>Never</span>;
  // make a date object with current time (as # of milliseconds since Jan 1, 1970)
  const currentTime = Date.now();
  // make a date object with last login date/time (as # of milliseconds since Jan 1, 1970)
  const lastLogin = new Date(props.value);
  // if last login happened in the last five minutes, show/return 'online'
  // (if # of milliseconds of last login is greater than (# of milliseconds of current time - 5 min))
  // 5 min * 60,000 milliseconds in a minute
  if (lastLogin > new Date(currentTime - 5 * 60000)) {
    // use span to set font color
    return <span style={{ color: "#1F711D" }}>Online</span>;
  }
  // if happened longer than 5 min ago, show/return date (MM/DD/YYYY)
  else {
    return formatDate(lastLogin);
  }
};

export const formatDate = (date: Date) => {
  return date.toLocaleDateString();
};
